import { httpClient } from '@/lib/httpClient';

export interface ProviderAnalytics {
  totalJobs: number;
  completedJobs: number;
  totalRevenue: number;
  averageJobValue: number;
  completionRate: number;
  customerSatisfaction: number;
  repeatCustomerRate: number;
  bookingConversionRate: number;
  averageResponseTime: number;
  revenueGrowth: number;
  jobGrowth: number;
  customerGrowth: number;
  topServices: Array<{
    service: string;
    count: number;
    revenue: number;
  }>;
  peakHours: Array<{
    hour: number;
    bookingCount: number;
  }>;
  period: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
  startDate: Date;
  endDate: Date;
}

class AnalyticsService {
  // Get revenue trends over time
  async getRevenueTrends(providerId: string, days: number): Promise<Array<{ date: string; revenue: number; jobs: number }>> {
    try {
      const response = await httpClient.get<Array<{ date: string; revenue: number; jobs: number }>>(`/api/v1/auth/providers/${providerId}/analytics/revenue-trends?days=${days}`);
      if (response.success && response.data) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch revenue trends:', error);
      return [];
    }
  }

  // Get booking funnel analytics
  async getBookingFunnel(providerId: string): Promise<Array<{ stage: string; count: number; conversionRate: number }>> {
    try {
      const response = await httpClient.get<Array<{ stage: string; count: number; conversionRate: number }>>(`/api/v1/auth/providers/${providerId}/analytics/booking-funnel`);
      if (response.success && response.data) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch booking funnel:', error);
      return [];
    }
  }

  // Get service performance breakdown
  async getServicePerformance(providerId: string, startDate: Date, endDate: Date): Promise<Array<{
    service: string;
    jobs: number;
    revenue: number;
    averageValue: number;
    satisfaction: number;
    growth: number;
  }>> {
    try {
      const response = await httpClient.get<Array<{
        service: string;
        jobs: number;
        revenue: number;
        averageValue: number;
        satisfaction: number;
        growth: number;
      }>>(`/api/v1/auth/providers/${providerId}/analytics/service-performance`, {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }
      });
      if (response.success && response.data) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch service performance:', error);
      return [];
    }
  }

  // Get customer insights
  async getCustomerInsights(providerId: string): Promise<{
    totalCustomers: number;
    newCustomers: number;
    repeatCustomers: number;
    averageLifetimeValue: number;
    topCustomers: Array<{
      name: string;
      jobs: number;
      totalSpent: number;
      lastService: Date;
    }>;
    customerSatisfaction: {
      average: number;
      distribution: Array<{ rating: number; count: number }>;
    };
  }> {
    try {
      const response = await httpClient.get<{
        totalCustomers: number;
        newCustomers: number;
        repeatCustomers: number;
        averageLifetimeValue: number;
        topCustomers: Array<{
          name: string;
          jobs: number;
          totalSpent: number;
          lastService: string;
        }>;
        customerSatisfaction: {
          average: number;
          distribution: Array<{ rating: number; count: number }>;
        };
      }>(`/api/v1/auth/providers/${providerId}/analytics/customer-insights`);
      
      if (response.success && response.data) {
        const data = response.data;
        return {
          totalCustomers: data.totalCustomers || 0,
          newCustomers: data.newCustomers || 0,
          repeatCustomers: data.repeatCustomers || 0,
          averageLifetimeValue: data.averageLifetimeValue || 0,
          topCustomers: (data.topCustomers || []).map((customer) => ({
            name: customer.name || '',
            jobs: customer.jobs || 0,
            totalSpent: customer.totalSpent || 0,
            lastService: new Date(customer.lastService || Date.now())
          })),
          customerSatisfaction: {
            average: data.customerSatisfaction?.average || 0,
            distribution: data.customerSatisfaction?.distribution || []
          }
        };
      }
      
      return {
        totalCustomers: 0,
        newCustomers: 0,
        repeatCustomers: 0,
        averageLifetimeValue: 0,
        topCustomers: [],
        customerSatisfaction: {
          average: 0,
          distribution: []
        }
      };
    } catch (error) {
      console.error('Failed to fetch customer insights:', error);
      return {
        totalCustomers: 0,
        newCustomers: 0,
        repeatCustomers: 0,
        averageLifetimeValue: 0,
        topCustomers: [],
        customerSatisfaction: {
          average: 0,
          distribution: []
        }
      };
    }
  }

  // Get peak hours analysis
  async getPeakHoursAnalysis(providerId: string): Promise<Array<{ hour: number; bookingCount: number; averageValue: number }>> {
    try {
      const response = await httpClient.get<Array<{ hour: number; bookingCount: number; averageValue: number }>>(`/api/v1/auth/providers/${providerId}/analytics/peak-hours`);
      if (response.success && response.data) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch peak hours analysis:', error);
      return [];
    }
  }

  // Get geographic performance
  async getGeographicPerformance(providerId: string): Promise<Array<{
    area: string;
    jobs: number;
    revenue: number;
    travelTime: number;
    satisfaction: number;
  }>> {
    try {
      const response = await httpClient.get<Array<{
        area: string;
        jobs: number;
        revenue: number;
        travelTime: number;
        satisfaction: number;
      }>>(`/api/v1/auth/providers/${providerId}/analytics/geographic-performance`);
      if (response.success && response.data) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch geographic performance:', error);
      return [];
    }
  }

  // Export analytics data
  async exportAnalytics(providerId: string, format: 'CSV' | 'PDF' | 'EXCEL'): Promise<string | null> {
    try {
      const response = await httpClient.get<{ downloadUrl: string }>(`/api/v1/auth/providers/${providerId}/analytics/export?format=${format.toLowerCase()}`);
      if (response.success && response.data?.downloadUrl) {
        return response.data.downloadUrl;
      }
      return null;
    } catch (error) {
      console.error('Failed to export analytics:', error);
      return null;
    }
  }

  // Get comprehensive analytics for a provider
  async getProviderAnalytics(
    providerId: string,
    period: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY',
    startDate: Date,
    endDate: Date
  ): Promise<ProviderAnalytics> {
    try {
      const response = await httpClient.get<{
        totalJobs: number;
        completedJobs: number;
        totalRevenue: number;
        averageJobValue: number;
        completionRate: number;
        customerSatisfaction: number;
        repeatCustomerRate: number;
        bookingConversionRate: number;
        averageResponseTime: number;
        revenueGrowth: number;
        jobGrowth: number;
        customerGrowth: number;
        topServices: Array<{ service: string; count: number; revenue: number }>;
        peakHours: Array<{ hour: number; bookingCount: number }>;
        startDate?: string;
        endDate?: string;
      }>(`/api/v1/auth/providers/${providerId}/analytics/comprehensive`, {
        params: {
          period: period.toLowerCase(),
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }
      });
      
      if (response.success && response.data) {
        const data = response.data;
        return {
          totalJobs: data.totalJobs || 0,
          completedJobs: data.completedJobs || 0,
          totalRevenue: data.totalRevenue || 0,
          averageJobValue: data.averageJobValue || 0,
          completionRate: data.completionRate || 0,
          customerSatisfaction: data.customerSatisfaction || 0,
          repeatCustomerRate: data.repeatCustomerRate || 0,
          bookingConversionRate: data.bookingConversionRate || 0,
          averageResponseTime: data.averageResponseTime || 0,
          revenueGrowth: data.revenueGrowth || 0,
          jobGrowth: data.jobGrowth || 0,
          customerGrowth: data.customerGrowth || 0,
          topServices: data.topServices || [],
          peakHours: data.peakHours || [],
          period,
          startDate: data.startDate ? new Date(data.startDate) : startDate,
          endDate: data.endDate ? new Date(data.endDate) : endDate
        };
      }
      
      // Return empty analytics if API fails
      return {
        totalJobs: 0,
        completedJobs: 0,
        totalRevenue: 0,
        averageJobValue: 0,
        completionRate: 0,
        customerSatisfaction: 0,
        repeatCustomerRate: 0,
        bookingConversionRate: 0,
        averageResponseTime: 0,
        revenueGrowth: 0,
        jobGrowth: 0,
        customerGrowth: 0,
        topServices: [],
        peakHours: [],
        period,
        startDate,
        endDate
      };
    } catch (error) {
      console.error('Failed to fetch provider analytics:', error);
      return {
        totalJobs: 0,
        completedJobs: 0,
        totalRevenue: 0,
        averageJobValue: 0,
        completionRate: 0,
        customerSatisfaction: 0,
        repeatCustomerRate: 0,
        bookingConversionRate: 0,
        averageResponseTime: 0,
        revenueGrowth: 0,
        jobGrowth: 0,
        customerGrowth: 0,
        topServices: [],
        peakHours: [],
        period,
        startDate,
        endDate
      };
    }
  }
}

export const analyticsService = new AnalyticsService();